import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { euro, fmtDate, workLabel } from "./format";
import { unitShort, isLumpSum, qtyWithUnit } from "./units";
import { legalNotesText } from "./calc";

const INDIGO = [62, 106, 136];
const SLATE = [43, 47, 51];

function priceWithUnit(value, unit, cur) {
  return `${euro(value, cur)}${isLumpSum(unit) ? "" : " /" + unitShort(unit)}`;
}

export function generateQuotePdf({ quote, issuer, totals, logo, brandLogo, asBlob = false }) {
  const cur = quote.currency || "EUR";
  const doc = new jsPDF();
  const W = doc.internal.pageSize.getWidth();
  let y = 18;

  doc.setFillColor(...INDIGO);
  doc.rect(0, 0, W, 4, "F");

  if (logo) {
    try {
      const props = doc.getImageProperties(logo);
      const maxW = 40;
      const maxH = 18;
      let w = maxW;
      let h = (props.height / props.width) * w;
      if (h > maxH) {
        h = maxH;
        w = (props.width / props.height) * h;
      }
      doc.addImage(logo, W - 14 - w, 12, w, h);
    } catch (e) {
      /* ignore logo errors */
    }
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(...SLATE);
  doc.text("Preventivo", 14, y);
  doc.setFontSize(11);
  doc.setTextColor(100);
  doc.text(quote.quote_number || "", 14, y + 6);
  y += 14;
  doc.setFontSize(12);
  doc.setTextColor(...INDIGO);
  doc.text(quote.title || "", 14, y);
  y += 10;

  doc.setFontSize(9);
  doc.setTextColor(120);
  doc.setFont("helvetica", "bold");
  doc.text("DA", 14, y);
  doc.text("PER", W / 2, y);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(60);
  const issuerLines = [
    issuer?.business_name || issuer?.display_name || issuer?.owner_name,
    issuer?.vat_number ? `P.IVA ${issuer.vat_number}` : null,
    issuer?.address,
    issuer?.email,
  ].filter(Boolean);
  const clientLines = [
    quote.client?.name,
    quote.client?.vat ? `P.IVA/CF ${quote.client.vat}` : null,
    quote.client?.address,
    quote.client?.email,
  ].filter(Boolean);
  const colW = W / 2 - 20;
  const iWrapped = issuerLines.flatMap((l) => doc.splitTextToSize(String(l), colW));
  const cWrapped = clientLines.flatMap((l) => doc.splitTextToSize(String(l), colW));
  doc.text(iWrapped, 14, y + 5);
  doc.text(cWrapped, W / 2, y + 5);
  y += 5 + Math.max(iWrapped.length, cWrapped.length) * 5 + 6;

  doc.setFontSize(9);
  doc.setTextColor(120);
  doc.text(`Scadenza: ${fmtDate(quote.due_date)}`, 14, y);
  y += 6;

  const rows = (totals.lines || [])
    .filter((l) => l.enabled)
    .map((l) => [
      l.title,
      qtyWithUnit(l.qty, l.unit),
      priceWithUnit(l.unit_price, l.unit, cur),
      l.discount > 0 ? `- ${euro(l.discount, cur)}` : "—",
      (l.work_days || 0) > 0 ? workLabel(l.work_days, l.work_unit) : "—",
      euro(l.lineTotal, cur),
    ]);

  autoTable(doc, {
    startY: y,
    head: [["Voce", "Q.tà", "Prezzo", "Sconto", "Tempistica", "Totale"]],
    body: rows,
    theme: "grid",
    headStyles: { fillColor: SLATE, textColor: 255, fontStyle: "bold" },
    styles: { fontSize: 9, cellPadding: 3 },
    columnStyles: {
      1: { halign: "center" },
      2: { halign: "right" },
      3: { halign: "right" },
      4: { halign: "center" },
      5: { halign: "right" },
    },
  });

  let fy = doc.lastAutoTable.finalY + 8;
  const rightX = W - 14;
  const labelX = W - 70;
  doc.setFontSize(10);
  doc.setTextColor(60);
  const row = (label, val, bold) => {
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.text(label, labelX, fy);
    doc.text(val, rightX, fy, { align: "right" });
    fy += 6;
  };
  row("Imponibile servizi", euro(totals.imponibile, cur));
  if (totals.scontoTotale > 0) row("Sconto applicato", `- ${euro(totals.scontoTotale, cur)}`);
  if (totals.contributo > 0) row(totals.contributoLabel || "Contributo 4%", euro(totals.contributo, cur));
  (totals.ivaBreakdown || []).forEach((b) => row(`IVA ${b.rate}%`, euro(b.iva, cur)));
  if (totals.bollo > 0) row("Marca da bollo", euro(totals.bollo, cur));
  doc.setDrawColor(200);
  doc.line(labelX, fy - 3, rightX, fy - 3);
  row("Totale documento", euro(totals.totaleLordo, cur));
  if (totals.ritenuta > 0) row(`Ritenuta d'acconto ${totals.ritenutaRate}%`, `- ${euro(totals.ritenuta, cur)}`);
  doc.setDrawColor(...INDIGO);
  doc.line(labelX, fy - 3, rightX, fy - 3);
  doc.setTextColor(...SLATE);
  row("NETTO A PAGARE", euro(totals.netto, cur), true);

  fy += 4;
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...SLATE);
  doc.setFontSize(10);
  doc.text("Condizioni di pagamento", 14, fy);
  fy += 6;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(60);
  (totals.installments || []).forEach((t) => {
    doc.text(`${t.label}: ${euro(t.amount, cur)}`, 14, fy);
    fy += 5;
  });
  doc.text(`Tempistica stimata: ${totals.deliveryDays} giorni lavorativi`, 14, fy + 2);
  fy += 2;

  if ((totals.ivaBreakdown || []).length > 0) {
    autoTable(doc, {
      startY: fy + 6,
      head: [["Riepilogo IVA", "Imponibile", "IVA"]],
      body: totals.ivaBreakdown.map((b) => [`Aliquota ${b.rate}%`, euro(b.imponibile, cur), euro(b.iva, cur)]),
      theme: "striped",
      headStyles: { fillColor: INDIGO, textColor: 255, fontStyle: "bold", fontSize: 8 },
      styles: { fontSize: 8, cellPadding: 2.5 },
      columnStyles: { 1: { halign: "right" }, 2: { halign: "right" } },
      tableWidth: 120,
      margin: { left: 14 },
    });
    fy = doc.lastAutoTable.finalY;
  }


  if (totals.ritenuta > 0) {
    fy += 7;
    doc.setTextColor(...INDIGO);
    doc.setFontSize(8.5);
    const rn = doc.splitTextToSize(
      `Ritenuta d'acconto ${totals.ritenutaRate}% (${euro(totals.ritenuta, cur)}) a carico del committente da versare tramite F24.`,
      W - 28
    );
    doc.text(rn, 14, fy);
    fy += rn.length * 4;
  }

  const legalText = legalNotesText(quote, totals);
  if (legalText) {
    fy += 7;
    if (fy > 270) { doc.addPage(); fy = 20; }
    doc.setTextColor(60);
    doc.setFontSize(8);
    doc.text("Note legali / Riferimenti normativi", 14, fy);
    fy += 4;
    doc.setTextColor(120);
    doc.setFontSize(7.5);
    const ln = doc.splitTextToSize(legalText, W - 28);
    doc.text(ln, 14, fy);
    fy += ln.length * 3.5;
  }

  if (quote.status === "accettato" && quote.accepted_name) {
    const at = quote.audit_trail || {};
    const hash = at.document_hash || quote.document_hash || "";
    if (fy > 230) { doc.addPage(); fy = 20; }
    fy += 10;

    const boxX = 14;
    const boxW = W - 28;
    const boxTop = fy;
    doc.setFillColor(...SLATE);
    doc.roundedRect(boxX, boxTop, boxW, 9, 1.5, 1.5, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.text("CERTIFICATO DI VALIDAZIONE ELETTRONICA (Regolamento UE eIDAS 910/2014)", boxX + 4, boxTop + 6);
    let cy = boxTop + 15;

    if (quote.signature_image) {
      try {
        const props = doc.getImageProperties(quote.signature_image);
        const maxW = 50, maxH = 18;
        let w = maxW, h = (props.height / props.width) * w;
        if (h > maxH) { h = maxH; w = (props.width / props.height) * h; }
        doc.setTextColor(120);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);
        doc.text("FIRMA", boxX + 4, cy);
        doc.addImage(quote.signature_image, "PNG", boxX + 4, cy + 1.5, w, h);
        cy += h + 5;
      } catch (e) { /* ignore */ }
    }

    const line = (label, val) => {
      if (!val) return;
      doc.setFont("helvetica", "bold");
      doc.setTextColor(100);
      doc.setFontSize(7.5);
      doc.text(label, boxX + 4, cy);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(40);
      const vs = doc.splitTextToSize(String(val), boxW - 50);
      doc.text(vs, boxX + 42, cy);
      cy += Math.max(vs.length * 3.6, 4.5);
    };
    line("Firmatario", `${quote.accepted_name}${at.signer_role || quote.accepted_role ? " — " + (at.signer_role || quote.accepted_role) : ""}`);
    line("Email", quote.accepted_email || at.signer_email);
    line("Data e ora", at.signed_at ? fmtDate(at.signed_at) : fmtDate(quote.accepted_at));
    line("Indirizzo IP", at.ip_address);
    line("Dispositivo", at.user_agent);
    const art1341 = at.accept_vexatious == null
      ? "Non applicabile (nessuna clausola vessatoria)"
      : (at.accept_vexatious ? "Approvata specificamente (artt. 1341-1342 c.c.)" : "NON approvata");
    line("Doppia approvazione", `Termini generali: accettati; Art. 1341 c.c.: ${art1341}`);

    cy += 1;
    doc.setFillColor(248, 250, 252);
    const hashLines = doc.splitTextToSize(hash, boxW - 14);
    const hashBoxH = 7 + hashLines.length * 3.4 + 2;
    doc.roundedRect(boxX + 4, cy, boxW - 8, hashBoxH, 1, 1, "F");
    doc.setFont("helvetica", "bold");
    doc.setTextColor(100);
    doc.setFontSize(7);
    doc.text("IMPRONTA DIGITALE CONTRATTO (SHA-256)", boxX + 7, cy + 5);
    doc.setFont("courier", "normal");
    doc.setTextColor(16, 185, 129);
    doc.setFontSize(7);
    doc.text(hashLines, boxX + 7, cy + 9.5);
    cy += hashBoxH + 3;

    doc.setDrawColor(203, 213, 225);
    doc.roundedRect(boxX, boxTop, boxW, cy - boxTop, 1.5, 1.5, "S");
    fy = cy + 4;
  }

  if (quote.payment?.status === "pagato") {
    fy += 8;
    const amt = quote.payment.deposit?.amount || 0;
    const m = quote.payment.method || "";
    const method = m === "bonifico" ? " tramite bonifico bancario" : m.startsWith("stripe") ? " con carta" : "";
    doc.setTextColor(4, 120, 87);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    const q = doc.splitTextToSize(
      `Quietanza acconto: ricevuto ${euro(amt, cur)}${method}${quote.payment.paid_at ? " il " + fmtDate(quote.payment.paid_at) : ""}.`,
      W - 28
    );
    doc.text(q, 14, fy);
  }

  if (quote.watermark) {
    const H = doc.internal.pageSize.getHeight();
    if (brandLogo) {
      try {
        const props = doc.getImageProperties(brandLogo);
        const lh = 6;
        const lw = (props.width / props.height) * lh;
        doc.addImage(brandLogo, "PNG", (W - lw) / 2, H - 8 - 3 - lh, lw, lh);
      } catch (e) { /* logo non disponibile: resta solo la riga di testo */ }
    }
    doc.setTextColor(150);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text("Creato con MyQuoteFlow - Preventivi interattivi, provalo gratis su myquoteflow.it", W / 2, H - 8, { align: "center" });
  }

  if (asBlob) return doc.output("blob");
  doc.save(`${quote.quote_number || "preventivo"}.pdf`);
}

export function generateCertificatePdf({ cert }) {
  const doc = new jsPDF();
  const W = doc.internal.pageSize.getWidth();
  let y = 0;

  doc.setFillColor(16, 185, 129);
  doc.rect(0, 0, W, 46, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.text("Certificato di Firma", W / 2, 24, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.text("Documento accettato e firmato elettronicamente", W / 2, 34, { align: "center" });

  y = 60;
  doc.setTextColor(100);
  doc.setFontSize(10);
  doc.text(cert.quote_number || "", W / 2, y, { align: "center" });
  doc.setTextColor(...SLATE);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.text(cert.title || "", W / 2, y + 8, { align: "center" });
  y += 20;

  if (cert.signature_image) {
    try {
      const props = doc.getImageProperties(cert.signature_image);
      const maxW = 70;
      const maxH = 28;
      let w = maxW;
      let h = (props.height / props.width) * w;
      if (h > maxH) {
        h = maxH;
        w = (props.width / props.height) * h;
      }
      doc.addImage(cert.signature_image, "PNG", (W - w) / 2, y, w, h);
      y += h + 8;
    } catch (e) {
      /* ignore */
    }
  }

  const rows = [
    ["Fornitore", cert.issuer_name],
    ["Cliente", cert.client_name],
    ["Firmatario", cert.signer_name],
    ["Email firmatario", cert.signer_email],
    ["Ruolo", cert.signer_role],
    ["Data e ora firma", cert.signed_at ? fmtDate(cert.signed_at) : ""],
    ["Indirizzo IP", cert.ip_address],
    ["Dispositivo", cert.user_agent],
  ].filter((r) => r[1]);

  autoTable(doc, {
    startY: y,
    body: rows,
    theme: "grid",
    styles: { fontSize: 9, cellPadding: 3, textColor: [43, 47, 51] },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 55, textColor: [115, 117, 117] },
      1: { cellWidth: W - 55 - 28 },
    },
    margin: { left: 14, right: 14 },
  });
  y = doc.lastAutoTable.finalY + 10;

  doc.setFillColor(...SLATE);
  doc.roundedRect(14, y, W - 28, 30, 2, 2, "F");
  doc.setTextColor(148, 163, 184);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text("SIGILLO DI INTEGRITA (SHA-256)", 20, y + 8);
  doc.setTextColor(52, 211, 153);
  doc.setFont("courier", "normal");
  doc.setFontSize(8);
  const hashLines = doc.splitTextToSize(cert.document_hash || "", W - 40);
  doc.text(hashLines, 20, y + 15);
  y += 38;

  doc.setTextColor(120);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  const note = doc.splitTextToSize(
    "Questo codice univoco certifica il contenuto esatto del documento firmato. Qualsiasi modifica al preventivo genererebbe un sigillo differente. Certificato generato automaticamente da MyQuoteFlow.",
    W - 28
  );
  doc.text(note, 14, y);

  doc.save(`certificato-${cert.quote_number || "firma"}.pdf`);
}
